import { useNavigation, useRoute } from '@react-navigation/native'
import React, { useCallback, useState } from 'react'

import { ActivityIdEnum } from 'api/gen'
import { useAuthContext } from 'features/auth/context/AuthContext'
import { Summary } from 'features/identityCheck/components/Summary'
import { getActivityLabel } from 'features/identityCheck/helpers/getActivityLabel'
import { useNavigateForwardToStepper } from 'features/identityCheck/helpers/useNavigateForwardToStepper'
import { useSaveStep } from 'features/identityCheck/pages/helpers/useSaveStep'
import { useStoredProfileInfos } from 'features/identityCheck/pages/helpers/useStoredProfileInfos'
import { ProfileTypes } from 'features/identityCheck/pages/profile/enums'
import {
  handlePostProfileError,
  handlePostProfileSuccess,
} from 'features/identityCheck/queries/profileSubmissionHandlers'
import { usePostProfileMutation } from 'features/identityCheck/queries/usePostProfileMutation'
import { IdentityCheckStep } from 'features/identityCheck/types'
import { UseNavigationType, UseRouteType } from 'features/navigation/navigators/RootNavigator/types'
import { getSubscriptionHookConfig } from 'features/navigation/navigators/SubscriptionStackNavigator/getSubscriptionHookConfig'
import { SuggestedCity } from 'libs/place/types'
import { ViewGap } from 'ui/components/ViewGap/ViewGap'
import { Button } from 'ui/designSystem/Button/Button'
import { PageWithHeader } from 'ui/pages/PageWithHeader'
import { Again } from 'ui/svg/icons/Again'

export const ProfileInformationValidationCreate = () => {
  const { refetchUser, user } = useAuthContext()
  const { navigateForwardToStepper } = useNavigateForwardToStepper()

  const { navigate, reset } = useNavigation<UseNavigationType>()
  const { params } = useRoute<UseRouteType<'ProfileInformationValidationCreate'>>()
  const type = params?.type ?? ProfileTypes.IDENTITY_CHECK // Fallback to most common scenario
  const profileOrigin = params?.origin
  const freeOfferId = params?.freeOfferId
  const isBookingFreeOffer = type === ProfileTypes.BOOKING_FREE_OFFER_15_16

  enum DataSources {
    LOCAL_STORAGE = 'localStorage',
    AUTH_CONTEXT = 'authContext',
  }

  const pageConfigByType = {
    [ProfileTypes.IDENTITY_CHECK]: {
      headerTitle: 'Profil',
      subtitle: 'Tu valides que ces informations sont correctes\u00a0?',
      formDataSource: DataSources.LOCAL_STORAGE,
    },
    [ProfileTypes.BOOKING_FREE_OFFER_15_16]: {
      headerTitle: 'Informations personnelles',
      subtitle: 'Tu valides que ces informations sont correctes\u00a0?',
      formDataSource: DataSources.LOCAL_STORAGE,
    },
    [ProfileTypes.RECAP_EXISTING_DATA]: {
      headerTitle: 'Informations personnelles',
      subtitle: 'Vérifie que ces informations sont correctes avant de continuer',
      formDataSource: DataSources.AUTH_CONTEXT,
    },
  }

  const storedProfileInfos = useStoredProfileInfos()
  const saveStep = useSaveStep()

  const shouldGetDataFromLocalStorage =
    pageConfigByType[type].formDataSource === DataSources.LOCAL_STORAGE
  const firstName = shouldGetDataFromLocalStorage
    ? storedProfileInfos?.name.firstName
    : user?.firstName
  const lastName = shouldGetDataFromLocalStorage
    ? storedProfileInfos?.name.lastName
    : user?.lastName
  const storedAddress = storedProfileInfos?.address ?? null
  const address = shouldGetDataFromLocalStorage ? storedAddress : user?.street
  const city = shouldGetDataFromLocalStorage ? storedProfileInfos?.city.name : user?.city
  const postalCode = shouldGetDataFromLocalStorage
    ? storedProfileInfos?.city.postalCode
    : user?.postalCode
  const activityId: ActivityIdEnum | null | undefined = shouldGetDataFromLocalStorage
    ? storedProfileInfos?.status
    : user?.activityId

  const { mutateAsync: postProfile } = usePostProfileMutation({
    onSuccess: () =>
      handlePostProfileSuccess({
        isBookingFreeOffer,
        profileOrigin,
        freeOfferId,
        navigateForwardToStepper,
        reset,
        refetchUser,
      }),
    onError: () =>
      handlePostProfileError({
        isBookingFreeOffer,
        freeOfferId,
        reset,
      }),
  })

  // isLoading from react-query is not support with mutateAsync
  const [isLoading, setIsLoading] = useState<boolean>(false)

  const submitProfileInfo = useCallback(async () => {
    if (!firstName || !lastName || !city || !postalCode || !address || !activityId) return

    const cityForPost: SuggestedCity = {
      name: city,
      postalCode,
      code: '', // not used in usePostProfileMutation but needed by typing
      departementCode: '',
    }
    const profile = {
      name: {
        firstName,
        lastName,
      },
      city: cityForPost,
      address: address,
      status: activityId,
      hasSchoolTypes: false,
      schoolType: null,
    }

    setIsLoading(true)
    await postProfile(profile)
    await saveStep(IdentityCheckStep.PROFILE)
    setIsLoading(false)
  }, [activityId, address, city, firstName, lastName, postProfile, postalCode, saveStep])

  const onSubmitProfile = () => submitProfileInfo()

  const getChangeInformationParams = () => {
    if (profileOrigin) {
      return freeOfferId
        ? { type, origin: profileOrigin, freeOfferId }
        : { type, origin: profileOrigin }
    }
    return freeOfferId ? { type, freeOfferId } : { type }
  }

  const onChangeInformation = () =>
    navigate(...getSubscriptionHookConfig('SetName', getChangeInformationParams()))

  const cityLabel = city && postalCode ? `${city} ${postalCode}` : undefined
  const activityLabel = activityId ? getActivityLabel(activityId) : undefined

  return (
    <PageWithHeader
      shouldLimitWidth
      title={pageConfigByType[type].headerTitle}
      scrollChildren={
        <Summary
          title={pageConfigByType[type].subtitle}
          data={[
            {
              title: 'Ton prénom',
              testID: 'validation-first-name',
              value: firstName,
            },
            {
              title: 'Ton nom de famille',
              testID: 'validation-name',
              value: lastName,
            },
            {
              title: 'Adresse',
              value: address,
            },
            {
              title: 'Ville de résidence',
              testID: 'validation-birth-date',
              value: cityLabel,
            },
            {
              title: 'Statut',
              value: activityLabel,
            },
          ]}
        />
      }
      fixedBottomChildren={
        <ViewGap gap={4}>
          <Button
            type="submit"
            wording="Continuer"
            onPress={onSubmitProfile}
            isLoading={isLoading}
          />
          <Button
            variant="tertiary"
            color="neutral"
            wording="Modifier mes informations"
            onPress={onChangeInformation}
            icon={Again}
          />
        </ViewGap>
      }
    />
  )
}
